import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

export const inputClass =
  'w-full px-3.5 py-2.5 rounded-[var(--radius-glass-sm)] bg-white border border-glass-border-bright text-sm text-text-primary placeholder:text-text-muted outline-none transition-all focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/10 disabled:bg-ink-muted disabled:text-text-muted';

function Label({ label, hint, required, children }: { label: string; hint?: ReactNode; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-text-primary mb-1.5">
        {label}
        {required && <span className="text-status-fault ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-text-muted mt-1.5">{hint}</span>}
    </label>
  );
}

export function TextField({ label, hint, ...props }: { label: string; hint?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Label label={label} hint={hint} required={props.required}>
      <input {...props} className={`${inputClass} ${props.className ?? ''}`} />
    </Label>
  );
}

export function TextArea({ label, hint, ...props }: { label: string; hint?: ReactNode } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <Label label={label} hint={hint} required={props.required}>
      <textarea rows={4} {...props} className={`${inputClass} resize-y ${props.className ?? ''}`} />
    </Label>
  );
}

export function SelectField({
  label,
  hint,
  options,
  ...props
}: { label: string; hint?: ReactNode; options: Array<{ value: string; label: string }> } & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <Label label={label} hint={hint} required={props.required}>
      <select {...props} className={`${inputClass} ${props.className ?? ''}`}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Label>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 w-10 h-6 rounded-full transition-colors shrink-0 ${checked ? 'bg-brand-primary' : 'bg-slate-300'}`}
      >
        <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </button>
      <span>
        <span className="block text-sm font-medium text-text-primary">{label}</span>
        {description && <span className="block text-xs text-text-muted mt-0.5">{description}</span>}
      </span>
    </label>
  );
}

export function Alert({ kind, children }: { kind: 'error' | 'success' | 'info'; children: ReactNode }) {
  const styles = {
    error: 'bg-status-fault/8 border-status-fault/25 text-status-fault',
    success: 'bg-status-running/8 border-status-running/25 text-status-running',
    info: 'bg-brand-primary/6 border-brand-primary/20 text-brand-primary',
  }[kind];
  const Icon = kind === 'success' ? CheckCircle2 : AlertCircle;
  return (
    <div role={kind === 'error' ? 'alert' : 'status'} className={`flex items-start gap-2.5 rounded-[var(--radius-glass-sm)] border px-3.5 py-3 text-sm ${styles}`}>
      <Icon size={16} className="mt-0.5 shrink-0" />
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}

/**
 * An off-screen field bots fill and people never see. The cloud drops any
 * submission where it is non-empty.
 */
export function Honeypot({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div aria-hidden className="absolute -left-[9999px] w-px h-px overflow-hidden">
      <label>
        Website URL
        <input tabIndex={-1} autoComplete="off" name="website_url" value={value} onChange={(e) => onChange(e.target.value)} />
      </label>
    </div>
  );
}
